// R53 — EVERY READING SHE EVER PAID FOR TRAVELS THIS ROUTE, AND TAPPING ONE COULD DO NOTHING AT ALL.
//
// THE HUNT is the pointer's, one route on from R51 and R52. `/api/astrology/reports` has **THIRTEEN**
// call sites in App.tsx (measured 2026-08-01; the SAVE half is already one transport — R47's
// `src/lib/saved-readings.ts` — so these thirteen are the LIST, the OPEN and the DELETE).
//
// **THE FINDING IS THE INVERSE OF R51's AND R52's, AND THAT IS WHY IT SURVIVED THEM BOTH.** Those two
// were surfaces that read NOTHING. Every one of these thirteen reads its payload. What each one does
// with a BAD answer is the defect, and it splits three ways:
//
//   * **THE LIST (five call sites).** Each wrote its own `.json().catch(() => ({ reports: [] }))`, so
//     **a failed read is an EMPTY SHELF rather than nothing-happened** — R52's ruling on
//     `listPeople`, one route over, on the list that holds everything she has ever generated. The
//     worst of the five is the **SIGNUP BUILD POLLER**: it paints `roomBuildReports` from that empty
//     list, so one lost request tells a brand-new member her reports are not ready, on the one
//     screen she is watching. Another blanked `reports` outright on a room load.
//   * **THE SINGLE REPORT (four call sites), AND TWO OF THEM OPENED NOTHING IN SILENCE.**
//     `openSavedReport` was `if (!response.ok) return;` then `catch { /* leave closed */ }`, and the
//     shelf drawer's `fetchReport` was `catch { return null; }` with `if (data) setOpenReport(data)`
//     after it. **She taps a reading she paid a generation for and the screen does not change** — no
//     modal, no sentence, no way to tell a broken tap from a slow one. That is R25's argument (an
//     absent control has no error state) applied to an absent RESULT.
//   * **THE DELETE (two).** Already honest since R35, and it is here so there is ONE answer-reader
//     for this route rather than two thirds of one.
//
// The route can answer FIVE ways and composes a sentence for four of them: **401**; **HTTP 200 with
// `{ ok: false }`** when `ensureUser` comes back empty (R39/R47/R51's shape, a FOURTH route);
// **404** "That reading is not yours to delete." / "Report not found."; **500**; and the plain 200.
//
// So the transport lives here ONCE and hands back an ANSWER rather than a status code (R31: the
// crossing stops existing rather than being watched). `saved-readings.ts`, `room-state.ts` and
// `people.ts` are the same file on other routes and the answer shape is deliberately theirs.
//
// This module imports NOTHING (the entry requirement for `scripts/`-loadable law).

// >>> IT #149 — THE SAVED READING KNOWS WHAT IT IS, AND THE ONE READER THAT OPENS IT THREW THAT
// AWAY. <<<
//
// `getReport` selects the row’s own `type` COLUMN and `sendJson(response, 200, report)` puts it on
// the wire beside `data`. `fetchSavedReport` returned `data.data` and nothing else — so every law
// downstream that is keyed on the type read `undefined` and took its DEFAULT, on a fact the server
// had already sent (R38/R165’s shape: the capability is carried and not read).
//
// **MEASURED ON THE LIVE TABLE, AND THE CLASS IS EXACT:** of 13,010 saved rows carrying data, 597
// across 185 members hold no `type` inside it — and 586 of those are the three CRAFT families,
// which are affected at ONE HUNDRED PER CENT (chant 425/425, affirmation 93/93, incantation 68/68),
// because `CraftDrawer` is the one saver in this house that puts the kind on the ROW and not in the
// body. The other eleven are old numerology, birth-chart and tarot rows.
//
// **WHAT IT COSTS HER, DRIVEN THROUGH THE REAL `ReportBody` WITH A MEMBER’S OWN ROW RATHER THAN
// DESCRIBED.** Her chant read back off the shelf renders
//     `12th House | Chant | GENERAL INFORMATION | Name: You | YOUR READING | <her words>`
// and with the type filled it renders
//     `12th House | Chant | YOUR CHANT | <her words>`.
// Both halves are W12-2’s own ruling arriving late: that round put `chant`, `affirmation` and
// `note` into `MAGIC_FAMILIES` precisely because *a Magic Room output carries THE ITEM, not the
// member* — and the repair could never fire for a SAVED craft reading, because the type never
// reached `hidesMemberIdentity`. The heading is the same round’s other named defect (*a herb view
// still read HERBS & TOOLS / Your Reading over a paragraph about rue*). `outputNoun` on the modal
// and `reportTypeLabel` (whose default is "Birth Chart" — R32) were reading the same nothing.
//
// >>> AND HER OWN DATA’S WORD WINS, ALWAYS. THAT CLAUSE IS NOT CAUTION, IT IS MEASURED. <<< Sixty-
// three live rows carry an inner type that DISAGREES with their column — 46 `human-design` rows
// whose body is a `human-design-compat`, and 17 `numerology-compatibility` rows whose body is a
// `numerology` — so a reader that preferred the column would re-route 46 compatibility reports to
// the wrong view. This FILLS AN ABSENCE and never overwrites an answer, which makes it provably
// neutral for all 12,413 rows that already carry one (ITEM 12’s neutrality argument).
//
// It is PRESENCE and never coercion (R53/R136/R137): only a real non-empty STRING is a type, so a
// `true`, a `1` or an object on either side fills nothing rather than stamping a reading with a
// word no roster contains. A row whose column is empty is left exactly as it is — nothing is
// invented for a reading nobody classified (R141).
//
// **THE SAVER IS DELIBERATELY NOT CHANGED, AND THAT IS DECLARED RATHER THAN LEFT AS AN ABSENCE**
// (R17/R160). This route is the ONLY door to a saved reading’s body — the list selects no `data`
// at all — so healing here reaches all 597 existing rows AND every future one, where a repair at
// `postReadingToRoom` would fix new rows and leave hers broken. A second stamp on the write side
// would be a second answer to which type a reading is (R28/R29).
export function readingWithType<T>(data: T, rowType: unknown): T {
  if (!data || typeof data !== "object" || Array.isArray(data)) return data;
  const inner = (data as { type?: unknown }).type;
  if (typeof inner === "string" && inner.trim() !== "") return data;
  if (typeof rowType !== "string" || rowType.trim() === "") return data;
  return { ...(data as Record<string, unknown>), type: rowType } as T;
}
export const reportsPath = "/api/astrology/reports";
export const reportPath = (id: string) => `${reportsPath}/${encodeURIComponent(id)}`;

// **THE SHELF DRAWER DELETES TWO KINDS THROUGH ONE CONTROL**, so the transport takes both or it does
// not fit (a seal lives in its own table and its own route). `handleDeleteGeneratedSeal` answers in
// the SAME shape `handleReports` does — 401, 400/404 `{ ok: false }`, 200 `{ ok: true }`, 500 — which
// is R35's "one house, one answer" written down; that is what makes one reader correct for both.
export const generatedSealPath = (id: string) => `/api/seals/generated/${encodeURIComponent(id)}`;
export type SavedKind = "seal" | "report";
export const savedItemPath = (kind: SavedKind, id: string) =>
  kind === "seal" ? generatedSealPath(id) : reportPath(id);

// FOUR answers, and the two in the middle are the ones a status code cannot tell apart.
//   sign-in — the session is gone (401). Nothing of hers is lost; she signs in and it opens.
//   refused — the server DELIBERATELY said no, at 200, 404 or 500, and it said why.
//   offline — the request never landed. Nothing was decided; transport noise (R18).
export type OpenReportAnswer<T> =
  | { ok: true; data: T }
  | { ok: false; kind: "sign-in" }
  | { ok: false; kind: "refused"; message: string }
  | { ok: false; kind: "offline" };

// The two sentences the house owns, handed in by whoever holds the string catalog — this module
// imports nothing and cannot read it (`saved-books.ts`'s fallback-parameter shape).
export type OpenReportCopy = { signIn: string; failed: string };

type Json = { ok?: boolean; message?: string; data?: unknown };

// THE LIST. **A FAILED READ IS NOT AN EMPTY SHELF** (R18, and R52's own ruling on `listPeople`): it
// answers null, so a surface keeps whatever it was already showing and a poller keeps polling,
// rather than painting "you have nothing" out of a request that lost its way.
//
// **R95 CORRECTED THE PREMISE THAT USED TO BE WRITTEN HERE, AND THE CORRECTION IS THE POINT.** This
// header said *"the route's own `ensureUser` early return sends a real `{ reports: [] }` and that IS
// an answer — an account with no rows"*. **It was not.** `ensureUser` comes back empty when there is
// no report storage at all, which R52, R57, R59 and R75 each ruled — on this exact guard, in this
// house's own words — is not a member with nothing saved. So the transport was carefully telling a
// transport failure apart from an empty shelf while the SERVER handed it the two collapsed into one,
// and the wrong premise sat in the file every future round on this route would read first. The route
// answers `{ ok: false }` with a sentence there now, so `reports` is ABSENT and this reader answers
// null — the shape check below was already right and only its reason was wrong.
//
// `doFetch` is a PARAMETER (R46's locked-door shape) so a gate can drive every answer the route can
// give without a browser, a session or a database.
export async function listSavedReports<T>(doFetch: typeof fetch = fetch): Promise<T[] | null> {
  try {
    const res = await doFetch(reportsPath, { cache: "no-store" });
    if (!res.ok) { await res.body?.cancel?.().catch(() => {}); return null; }
    const data = (await res.json().catch(() => null)) as { reports?: unknown } | null;
    return Array.isArray(data?.reports) ? (data.reports as T[]) : null;
  } catch {
    return null;
  }
}

// THE OPEN, and it is the half that is a capability rather than a tidy-up. Four call sites asked for
// one stored reading and two of them said nothing whatsoever when it did not come.
//
// **A 200 CARRYING NO `data` IS A REFUSAL, NOT A SUCCESS.** That is R18's seal-status lesson exactly
// (a `ready` with no image URL became five minutes of nothing): the row exists in the shape the
// route returns, so an absent `data` is a malformed answer and there is nothing to render. It must
// reach her as a sentence, because the alternative is the silence this round exists to end.
export async function fetchSavedReport<T>(
  id: string,
  doFetch: typeof fetch = fetch,
): Promise<OpenReportAnswer<T>> {
  try {
    const res = await doFetch(reportPath(id), { cache: "no-store" });
    if (res.status === 401) { await res.body?.cancel?.().catch(() => {}); return { ok: false, kind: "sign-in" }; }
    const data = (await res.json().catch(() => ({}))) as Json;
    if (res.ok && data?.ok !== false && data?.data !== undefined && data?.data !== null) {
      return { ok: true, data: readingWithType(data.data, (data as { type?: unknown }).type) as T };
    }
    return { ok: false, kind: "refused", message: typeof data?.message === "string" ? data.message : "" };
  } catch {
    return { ok: false, kind: "offline" };
  }
}

// THE DELETE. R35's law, unchanged and now shared: the row comes off her screen ONLY if the server
// really took it. TRUE only when the server said the row is gone.
export async function deleteSavedItem(
  kind: SavedKind,
  id: string,
  doFetch: typeof fetch = fetch,
): Promise<boolean> {
  try {
    const res = await doFetch(savedItemPath(kind, id), { method: "DELETE" });
    const data = (await res.json().catch(() => ({}))) as Json;
    return res.ok && data?.ok !== false;
  } catch {
    return false;
  }
}

// The sentence, derived from the answer rather than typed per surface. **A SUCCESS SAYS NOTHING** —
// the reading itself opening is the whole answer, and a line under it would be noise (R51's ruling
// on the same question). Only a failure has something to tell her, and until this round it had no
// way to tell her at all.
export function openReportNote<T>(answer: OpenReportAnswer<T>, copy: OpenReportCopy): string {
  if (answer.ok) return "";
  if (answer.kind === "sign-in") return copy.signIn;
  if (answer.kind === "refused" && answer.message) return answer.message;
  return copy.failed;
}
